import { FC, useState, useEffect, useRef } from 'react';
import { useAppSelector, useEnhancedTranslation } from '@app/hooks';
import './current-order.scss';
import { Link, useParams } from 'react-router-dom';
import { NewButton } from '@app/components/NewButton/NewButton';
import { cancelOrder, getOrderData, paymentOrder, TOrderListItem } from '@app/api/orders/orders.service';
import { formatDateTimeV2, formatDateV3, formatNumberWithSpaces, formatPhoneNumber } from '@app/utils';
import { isSameDay } from '@app/components/ymap-widget/helpers';
import { getOrderListItemSum, getOrderTabComponentStatus } from '../order/helpers';

export const CurrentOrder: FC = () => {
  const { t } = useEnhancedTranslation({ keyPrefix: 'account.orders.orderPage' });
  const { t: tMonth } = useEnhancedTranslation({ keyPrefix: 'month' });
  const { id } = useParams();
  const isInitialMount = useRef(true);
  const prevIdRef = useRef(id);
  const {
    authReducer: {
      access: { accessToken },
    },
  } = useAppSelector((state) => state);

  const [orderData, setOrderData] = useState<TOrderListItem | null>(null);

  async function getData(currentId: string, token?: string): Promise<void> {
    try {
      const res = await getOrderData(currentId, token);
      setOrderData(res);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (isInitialMount.current && accessToken) {
      isInitialMount.current = false;
      getData(id || '', accessToken);
    } else if (prevIdRef.current !== id && accessToken) {
      getData(id || '', accessToken);
    }
    prevIdRef.current = id;
  }, [id, accessToken]);

  function getDeliveryType(data: TOrderListItem): string | undefined | null {
    if (data.orderType === 'gift_card_digital') {
      return t('deliveryBlock.electronic');
    }
    if (data.orderInfo.deliveryType === 'pickup') {
      return t('deliveryBlock.shop');
    }
    if (data.orderInfo.deliveryType === 'delivery') {
      return t('deliveryBlock.courier');
    }
    return null;
  }

  async function onCancelBtn(orderId: string, token: string): Promise<void> {
    try {
      await cancelOrder(orderId, token);
    } catch (error) {
      console.log(error);
    } finally {
      getData(id || '', accessToken || '');
    }
  }

  async function onPaymentBtn(orderId: string, token: string): Promise<void> {
    try {
      const res = await paymentOrder(orderId, token);
      if (res?.invoice_url) {
        window.location.href = res?.invoice_url;
      }
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="current-order">
      <div className="current-order__title-wrap">
        <Link to="/lk/orders" className="current-order__back-link">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
            <path
              d="M3.43323e-05 20L12 8L12.1263 8.12632C12.1263 8.12632 5.55792 14.7263 5.55792 20C5.55792 25.2737 12.1263 31.8737 12.1263 31.8737L12 32L3.43323e-05 20Z"
              fill="#1F1F1F"
            />
            <path d="M5.55792 19.6842H40V20.2842H5.55792V19.6842Z" fill="#1F1F1F" />
          </svg>
          <span>{t('backBtn')}</span>
        </Link>
        <h1 className="current-order__title">
          {t('title')} {orderData?.orderNumber}
        </h1>
      </div>
      {orderData && (
        <>
          <div
            className={`current-order__item-status current-order__item-status--${getOrderTabComponentStatus(
              orderData?.status,
            )}`}
          >
            {t(`statuses.${getOrderTabComponentStatus(orderData?.status)}`)}
          </div>
          <div className="current-order__main-wrap">
            <div className="current-order__info-wrap">
              <div className="current-order__info-block">
                <div className="current-order__info-title">{t('deliveryBlock.title')}</div>
                <div className="current-order__info-subtitle">{getDeliveryType(orderData)}</div>
                <div className="current-order__info-block-list">
                  {orderData?.orderType === 'gift_card_digital' && (
                    <>
                      {orderData?.items[0]?.cardUsage === 'gift' && (
                        <>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">{t('deliveryBlock.tel')}:</div>
                            <div>{formatPhoneNumber(orderData?.items[0]?.phone || '')}</div>
                          </div>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">
                              {t('deliveryBlock.emailRecipient')}
                            </div>
                            <div>{orderData?.items[0]?.email}</div>
                          </div>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">{t('deliveryBlock.recipient')}:</div>
                            <div>{orderData?.items[0]?.recipientName}</div>
                          </div>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">
                              {t('deliveryBlock.greetingText')}:
                            </div>
                            <div>{orderData?.items[0]?.message}</div>
                          </div>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">{t('deliveryBlock.sender')}:</div>
                            <div>{orderData?.items[0]?.senderName}</div>
                          </div>
                        </>
                      )}
                      {orderData?.items[0]?.cardUsage === 'yourself' && (
                        <>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">{t('deliveryBlock.recipient')}:</div>
                            <div>{orderData?.orderInfo?.customerFullName}</div>
                          </div>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">{t('deliveryBlock.tel')}:</div>
                            <div>{formatPhoneNumber(orderData?.orderInfo?.customerPhone)}</div>
                          </div>
                        </>
                      )}
                      {orderData?.items[0]?.whenDeliver === 'now' ? (
                        <div className="current-order__info-block-list-item">
                          <div className="current-order__info-block-list-title">{t('deliveryBlock.dateDelivery')}:</div>
                          <div>
                            {isSameDay(new Date(), new Date(orderData?.createdAt)) &&
                              `${t('deliveryBlock.dateDeliveryText1')}, `}
                            {formatDateV3(new Date(orderData?.createdAt), tMonth)}
                            {isSameDay(new Date(), new Date(orderData?.createdAt)) &&
                              `, ${t('deliveryBlock.dateDeliveryText2')}`}
                          </div>
                        </div>
                      ) : (
                        <div className="current-order__info-block-list-item">
                          <div className="current-order__info-block-list-title">{t('deliveryBlock.dateDelivery')}:</div>
                          <div>{formatDateTimeV2(orderData?.items[0]?.dateTime || '', tMonth)}</div>
                        </div>
                      )}
                      {/* <div className="current-order__info-block-list-item">
                        <div className="current-order__info-block-list-title">{t('deliveryBlock.tel')}:</div>
                        <div>{formatPhoneNumber(orderData.orderInfo.customerPhone)}</div>
                      </div>
                      <div className="current-order__info-block-list-item">
                        <div className="current-order__info-block-list-title">{t('deliveryBlock.emailRecipient')}</div>
                        <div>{orderData.orderInfo.customerEmail}</div>
                      </div>
                      <div className="current-order__info-block-list-item">
                        <div className="current-order__info-block-list-title">{t('deliveryBlock.recipient')}:</div>
                        <div>{orderData.orderInfo.customerFullName}</div>
                      </div> */}
                    </>
                  )}
                  {orderData?.orderType === 'gift_card_physical' && (
                    <>
                      <div className="current-order__info-block-list-item">
                        <div className="current-order__info-block-list-title">{t('deliveryBlock.buyer')}:</div>
                        <div>{orderData.orderInfo.customerFullName}</div>
                      </div>
                      <div className="current-order__info-block-list-item">
                        <div className="current-order__info-block-list-title">{t('deliveryBlock.telBuyer')}:</div>
                        <div>{formatPhoneNumber(orderData.orderInfo.customerPhone)}</div>
                      </div>
                      <div className="current-order__info-block-list-item">
                        <div className="current-order__info-block-list-title">{t('deliveryBlock.emailBuyer')}:</div>
                        <div>{orderData.orderInfo.customerEmail}</div>
                      </div>
                      {orderData.orderInfo.deliveryType === 'delivery' ? (
                        <>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">{t('deliveryBlock.address')}:</div>
                            <div>
                              {orderData.orderInfo.address}, {orderData.orderInfo.addressMetadata}
                            </div>
                          </div>
                          {orderData.orderInfo.deliveryComment && (
                            <div className="current-order__info-block-list-item">
                              <div className="current-order__info-block-list-title">
                                {t('deliveryBlock.deliveryComment')}:
                              </div>
                              <div>{orderData.orderInfo.deliveryComment}</div>
                            </div>
                          )}
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">{t('deliveryBlock.timeTitle')}:</div>
                            <div>{t('deliveryBlock.timeText')}</div>
                          </div>
                          {orderData?.orderInfo?.comment && (
                            <div className="current-order__info-block-list-item">
                              <div className="current-order__info-block-list-title">{t('deliveryBlock.comment')}:</div>
                              <div>{orderData?.orderInfo?.comment}</div>
                            </div>
                          )}
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">
                              {t('deliveryBlock.deliveryCost')}:
                            </div>
                            <div>{orderData?.orderInfo?.deliveryPrice} ₽</div>
                          </div>
                        </>
                      ) : (
                        <>
                          {' '}
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">
                              {t('deliveryBlock.shopAddress')}:
                            </div>
                            <div>{t('deliveryBlock.shopAddressText')}</div>
                          </div>
                          <div className="current-order__info-block-list-item">
                            <div className="current-order__info-block-list-title">{t('deliveryBlock.pickupTime')}:</div>
                            <div>{t('deliveryBlock.pickupTimeText')}</div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="current-order__list-block">
                <div className="current-order__info-title">{t('cardsBlock.title')}</div>
                <div className="current-order__cards-wrap">
                  {orderData.items?.map((item) => (
                    <div className="current-order__card" key={item.product._id}>
                      <div className="current-order__card-image">
                        <img src={`${process.env.REACT_APP_IMAGES_URL}/${item.product.images[0]?.url}`} alt="" />
                      </div>
                      <div className="current-order__card-info">
                        <div className="current-order__card-price">
                          {formatNumberWithSpaces(
                            item.cardType === 'digital'
                              ? item.nominal.toString()
                              : (item.nominal * item.count).toString(),
                          )}{' '}
                          ₽
                        </div>
                        {item.cardType === 'physical' ? (
                          <div className="current-order__card-count">
                            {formatNumberWithSpaces(item.nominal.toString())} ₽ x {item.count}
                          </div>
                        ) : (
                          <div className="current-order__card-count">
                            {formatNumberWithSpaces(item.nominal.toString())} ₽ x 1
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="current-order__total-block">
              <div className="current-order__info-title">{t('paymentBlock.title')}</div>
              <div className="current-order__total-cost-block">
                <div className="current-order__total-cost-item">
                  <span>{t('paymentBlock.cardPrice')}</span>
                  <span>{formatNumberWithSpaces(getOrderListItemSum(orderData).toString())} ₽</span>
                </div>
                {orderData.orderInfo.deliveryType === 'delivery' && (
                  <div className="current-order__total-cost-item">
                    <span>{t('paymentBlock.deliveryPrice')}</span>
                    <span>{formatNumberWithSpaces((orderData.orderInfo.deliveryPrice || 0)?.toString())} ₽</span>
                  </div>
                )}
              </div>
              <div className="current-order__total-sum-wrap">
                <span>{t('paymentBlock.total')}</span>
                <span>
                  {formatNumberWithSpaces(
                    (getOrderListItemSum(orderData) + (orderData.orderInfo.deliveryPrice || 0)).toString(),
                  )}{' '}
                  ₽
                </span>
              </div>
              {orderData.status === 'not_paid' && (
                <div className="current-order__total-btn-wrap">
                  <NewButton size="medium" onClick={() => onPaymentBtn(orderData._id, accessToken || '')}>
                    {t('paymentBlock.paymentBtn')}
                  </NewButton>
                  <NewButton
                    size="medium"
                    variant="outline-cancel"
                    onClick={() => onCancelBtn(orderData._id, accessToken || '')}
                  >
                    {t('paymentBlock.cancelBtn')}
                  </NewButton>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
